import type { DashboardMode, FilterPresetDto, FilterPresetPayload } from '@nexus/contracts';
import { FilterPresetPayloadSchema } from '@nexus/contracts';
import {
  appliedStateToExploreSearch,
  exploreSearchEquals,
  type ExploreSearch,
} from '@/features/dashboard/lib/explore-search';
import { useDashboardFiltersStore } from '@/features/dashboard/stores/dashboardFilters.store';
import { withSnapshotDates } from '@/features/dashboard/lib/explore-date-presets';
import type { CompetitorFilters, MyBrandFilters } from '@/features/dashboard/types/dashboard.types';
import {
  DEFAULT_COMPETITOR_FILTERS,
  DEFAULT_MY_BRAND_FILTERS,
} from '@/features/dashboard/types/dashboard.types';
import {
  overviewAppliedKey,
  selectOverviewDraftKey,
  useOverviewFiltersStore,
} from '@/features/overview/stores/overviewFilters.store';

/** Include custom dates so a leftover 90d token cannot match a saved custom window. */
const PRESET_MATCH_SEARCH = { persistCustomWindow: true } as const;

export function buildCurrentPresetPayload(mode: DashboardMode): FilterPresetPayload {
  const overview = useOverviewFiltersStore.getState();
  const dashboard = useDashboardFiltersStore.getState();
  const page = overview.applied;

  if (mode === 'my-brand') {
    return FilterPresetPayloadSchema.parse({
      mode: 'my-brand',
      page,
      global: dashboard.appliedMyBrandFilters,
    });
  }

  return FilterPresetPayloadSchema.parse({
    mode: 'competitors',
    page,
    global: dashboard.appliedCompetitorFilters,
  });
}

export function applyFilterPreset(preset: FilterPresetDto): ExploreSearch {
  const { filters } = preset;
  const dashboard = useDashboardFiltersStore.getState();

  useOverviewFiltersStore.getState().loadPresetState(filters.page);

  if (filters.mode === 'my-brand') {
    const myBrandFilters = withSnapshotDates(filters.global as MyBrandFilters);
    useDashboardFiltersStore.getState().hydrateFromApplied({
      mode: 'my-brand',
      competitorFilters: dashboard.appliedCompetitorFilters,
      myBrandFilters,
    });
    useDashboardFiltersStore.getState().applyFilters();
    return appliedStateToExploreSearch('my-brand', dashboard.appliedCompetitorFilters, myBrandFilters);
  }

  const competitorFilters = withSnapshotDates(filters.global as CompetitorFilters);
  useDashboardFiltersStore.getState().hydrateFromApplied({
    mode: 'competitors',
    competitorFilters,
    myBrandFilters: dashboard.appliedMyBrandFilters,
  });
  useDashboardFiltersStore.getState().applyFilters();
  return appliedStateToExploreSearch('competitors', competitorFilters, dashboard.appliedMyBrandFilters);
}

/** True when live draft filters match a preset's durable URL + page state (for post-refresh restore). */
export function presetMatchesCurrentFilters(preset: FilterPresetDto): boolean {
  const overview = useOverviewFiltersStore.getState();
  const dashboard = useDashboardFiltersStore.getState();
  const presetMode = preset.filters.mode;

  if (selectOverviewDraftKey(overview) !== overviewAppliedKey(preset.filters.page)) {
    return false;
  }

  const live =
    presetMode === 'my-brand'
      ? appliedStateToExploreSearch(
          'my-brand',
          dashboard.competitorFilters,
          dashboard.myBrandFilters,
          PRESET_MATCH_SEARCH,
        )
      : appliedStateToExploreSearch(
          'competitors',
          dashboard.competitorFilters,
          dashboard.myBrandFilters,
          PRESET_MATCH_SEARCH,
        );
  const wanted =
    presetMode === 'my-brand'
      ? appliedStateToExploreSearch(
          'my-brand',
          DEFAULT_COMPETITOR_FILTERS,
          withSnapshotDates(preset.filters.global as MyBrandFilters),
          PRESET_MATCH_SEARCH,
        )
      : appliedStateToExploreSearch(
          'competitors',
          withSnapshotDates(preset.filters.global as CompetitorFilters),
          DEFAULT_MY_BRAND_FILTERS,
          PRESET_MATCH_SEARCH,
        );

  return exploreSearchEquals(live, wanted);
}
